import React from 'react';
import { CheckboxV2, Flex, TextV2 } from '@withjoy/joykit';
import { useTranslation } from '@shared/core';
import { HONEYMOON_TAG } from '@shared/constants/accommodations';

type PrioritizeTagsFacetProps = Readonly<{
  /** Tags offered as choices, in display order. Rendering is skipped entirely when empty. */
  availableTags: ReadonlyArray<string>;
  /** Tags currently leading the results, read from `?prioritizeTag=`. */
  selectedTags: ReadonlyArray<string>;
  onToggle: (tag: string, isSelected: boolean) => void;
}>;

/**
 * Chooses which curated tags lead the results — the UI over `?prioritizeTag=`.
 *
 * A boost, not a filter, and the copy says so: unchecking does not hide anything, it only stops
 * those stays being lifted. That distinction is why this sits above the real filters rather than
 * among them, and why "Clear all" leaves it alone (see FiltersPanel.onReset).
 */
export const PrioritizeTagsFacet: React.FC<PrioritizeTagsFacetProps> = ({ availableTags, selectedTags, onToggle }) => {
  const { tq } = useTranslation('accommodationsConsole');

  if (!availableTags.length) {
    return null;
  }

  // Keyed off the tag rather than a label map so an unrecognized tag still renders something
  // honest instead of a blank row.
  const labelForTag = (tag: string): string => {
    switch (tag) {
      case HONEYMOON_TAG:
        return tq`Honeymoon stays`;
      default:
        return tag;
    }
  };

  const selected = new Set(selectedTags);

  return (
    <Flex
      flexDirection="column"
      gap={3}
    >
      <TextV2 typographyVariant="hed2">{tq`Show first`}</TextV2>
      {availableTags.map(tag => (
        <CheckboxV2
          key={tag}
          id={`prioritize-tag-${tag}`}
          isChecked={selected.has(tag)}
          onChange={event => onToggle(tag, event.target.checked)}
        >
          <TextV2
            color="mono14"
            typographyVariant="body1"
          >
            {labelForTag(tag)}
          </TextV2>
        </CheckboxV2>
      ))}
      <TextV2
        color="mono12"
        typographyVariant="label2"
      >
        {tq`These stays move to the top. Nothing is hidden.`}
      </TextV2>
    </Flex>
  );
};
